import { Market } from "../Market";
import { observable, action, computed } from "mobx";
import { LiveGameState } from "../LiveGameState";
import { parseTimeFull } from "sb-resp-lib";
import { Scoreboard } from "../Scoreboard";
import { GameStatus } from "../../helpers";
import { MasterEventInitialData, MasterEventTypeInitial } from "./MasterEventInitialData";

/**
* MasterEvent defines model for master object information
*
* @typedef MasterEvent
* @type {Object}
* @property {id} EventId identifier
* @property {team1Name} Name of the first team
* @property {team2Name} Name of the second team
* @property {leagueID} Id of the league
* @property {leagueName} Name of the league
* @property {eventDate} Date when event will start in requested format
* @property {sportID} ID of the sport
* @property {sportName} Name of the sport
* @property {markets} List of markets for this event
* @property {isTeamSwapEnabled} If team swapping is enabled
* @property {isLive} If event is live or prelive
*/

export type MasterEventType = MasterEventTypeInitial & {
    isLive: boolean;
    liveGameState?: LiveGameState;
    liveStartCountdown: number;
    status: GameStatus;
    markets: Market[];
    score1?: string;
    score2?: string;
    scoreboard?: Scoreboard;
}

export class MasterEvent extends MasterEventInitialData {
    @observable score1?: string;
    @observable score2?: string;
    @observable scoreboard?: Scoreboard;
    @observable markets: Market[];
    @observable isLive: boolean;
    @observable liveGameState?: LiveGameState;
    @observable status: GameStatus;
    @observable liveStartCountdown: number;

    constructor(masterEventData: MasterEventType) {
        super(masterEventData);

        this.score1 = masterEventData.score1;
        this.score2 = masterEventData.score2;
        this.scoreboard = masterEventData.scoreboard;
        this.isLive = masterEventData.isLive;
        this.markets = masterEventData.markets;
        this.liveGameState = masterEventData.liveGameState;
        this.liveStartCountdown = masterEventData.liveStartCountdown || 0;
        this.status = masterEventData.status;
    }

    @action.bound
    update(masterEvent: MasterEvent): void {
        this.score1 = masterEvent.score1;
        this.score2 = masterEvent.score2;
        this.isLive = masterEvent.isLive;
        this.liveStartCountdown = masterEvent.liveStartCountdown;
        this.updateLiveGameState(masterEvent);
        this.scoreboard = masterEvent.scoreboard;
        this.status = masterEvent.status
    }

    @action.bound
    updateLiveStartCountdown(liveStartCountdown: number): void {
        this.liveStartCountdown = liveStartCountdown;
    }

    private updateLiveGameState(masterEvent: MasterEvent): void {
        this.liveGameState && masterEvent.liveGameState
            ? this.liveGameState.update(masterEvent.liveGameState)
            : (this.liveGameState = masterEvent.liveGameState);
    }

    removeMarkets(marketIds: string[]): void {
        for (let i: number = 0; i < marketIds.length; i++) {
            let market2removeIndex: number = this.markets.map(m => m.id).indexOf(marketIds[i]);
            if (market2removeIndex !== -1) {
                this.markets.splice(market2removeIndex, 1);
            }
        }
    }

    upsertMarket(market: Market): void {
        let existingMarkets: string[] = this.markets.map(m => m.id);
        let existingMarketIndex: number = existingMarkets.indexOf(market.id);
        existingMarketIndex !== -1
            ? this.markets[existingMarketIndex].update(market)
            : this.markets.push(market);
    }

    @computed get formattedCountdown(): string {
        return parseTimeFull(this.liveStartCountdown);
    }
}
